import { useEffect, useState } from 'react';
import { logger } from '../utils/logger';
import { Store, Plus } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { prepareCheckoutWindow, completeCheckoutRedirect, closeCheckoutWindow, isStandalonePWA } from '../utils/pwaCheckout';
import {
  MarketplaceStats,
  MarketplaceTabs,
  ListingCard,
  MyListingCard,
  TransactionCard,
  EmptyState,
  CreateListingModal
} from '../components/marketplace';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function MarketplacePage() {
  const { isAuthenticated } = useAuth();
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [ownedTokens, setOwnedTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('browse');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      const [listingsRes] = await Promise.all([
        axios.get(`${API_URL}/marketplace/listings`)
      ]);
      setListings(listingsRes.data);

      if (isAuthenticated) {
        const [myListingsRes, txRes, tokensRes] = await Promise.all([
          axios.get(`${API_URL}/marketplace/my-listings`),
          axios.get(`${API_URL}/marketplace/transactions`),
          axios.get(`${API_URL}/tokens/owned`)
        ]);
        setMyListings(myListingsRes.data);
        setTransactions(txRes.data);
        setOwnedTokens(tokensRes.data);
      }
    } catch (error) {
      logger.error('Failed to fetch marketplace data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateListing = async (listingData) => {
    try {
      await axios.post(`${API_URL}/marketplace/list`, listingData);
      toast.success('Listing created successfully!');
      setShowCreateModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    }
  };

  const handleBuy = async (listing) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to buy tokens');
      return;
    }

    // Open the PWA tab synchronously (fixes iOS home-screen silent-fail)
    const pwaWindow = prepareCheckoutWindow();
    try {
      const res = await axios.post(`${API_URL}/marketplace/buy`, {
        listing_id: listing.id,
        origin_url: window.location.origin
      });

      if (res.data.checkout_url) {
        if (isStandalonePWA()) {
          toast.info('Opening secure checkout in your browser. Come back here when done.');
        }
        completeCheckoutRedirect(pwaWindow, res.data.checkout_url);
      } else {
        closeCheckoutWindow(pwaWindow);
      }
    } catch (error) {
      closeCheckoutWindow(pwaWindow);
      toast.error(error.response?.data?.detail || 'Failed to start purchase');
    }
  };

  const handleCancelListing = async (listingId) => {
    if (!window.confirm('Cancel this listing?')) return;
    
    try {
      await axios.delete(`${API_URL}/marketplace/listings/${listingId}`);
      toast.success('Listing cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel listing');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="main-content pt-24" data-testid="marketplace-page">
      <div className="container py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
              <Store className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Token Marketplace</h1>
              <p className="text-gray-400">Buy and sell fractional ownership tokens</p>
            </div>
          </div>
          
          {isAuthenticated && ownedTokens.length > 0 && (
            <button 
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="create-listing-btn"
            >
              <Plus className="w-4 h-4" /> List Tokens
            </button>
          )}
        </div>

        <MarketplaceStats 
          listings={listings}
          ownedTokens={ownedTokens}
          myListings={myListings}
          transactions={transactions}
        />

        <MarketplaceTabs activeTab={activeTab} setActiveTab={setActiveTab} />

        {/* Content */}
        {activeTab === 'browse' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.length === 0 ? (
              <EmptyState type="browse" />
            ) : (
              listings.map(listing => (
                <ListingCard 
                  key={listing.id} 
                  listing={listing} 
                  onBuy={handleBuy} 
                />
              ))
            )}
          </div>
        )}

        {activeTab === 'my-listings' && (
          <div className="space-y-4">
            {myListings.length === 0 ? (
              <EmptyState type="my-listings" />
            ) : (
              myListings.map(listing => (
                <MyListingCard 
                  key={listing.id} 
                  listing={listing} 
                  onCancel={handleCancelListing} 
                />
              ))
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            {transactions.length === 0 ? (
              <EmptyState type="history" />
            ) : (
              transactions.map(tx => (
                <TransactionCard key={tx.id} tx={tx} />
              ))
            )}
          </div>
        )}
      </div>

      <CreateListingModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        ownedTokens={ownedTokens}
        onCreateListing={handleCreateListing}
      />
    </div>
  );
}
